import React, { useCallback, useEffect, useRef, useState } from 'react'
import { faChevronRight, faExclamation } from '@fortawesome/free-solid-svg-icons'
import { FontAwesomeIcon } from '@fortawesome/react-fontawesome'
import mapsLoader from '../lib/maps-api-loader'

export default function PlacePicker({
  displayOnly,
  address,
  onSelectAddress,
  label = ' ',
  persistentMarker,
  disableGesture,
}) {
  const [geolocationError, setGeolocationError] = useState()

  const [map, setMap] = useState()
  const [defaultCenterSet, setDefaultCenterSet] = useState(false)
  const geocoder = useRef()

  const pickupMarker = useRef()
  const baseMarker = useRef()

  const placeAutoCompleteRef = useRef()
  const regionViewRef = useRef()

  const reverseGeocodeTimeout = useRef()

  const googleApiKey = process.env.NEXT_PUBLIC_GOOGLE_API_KEY

  const setUpMap = useCallback(async () => {
    await mapsLoader.initialize(googleApiKey)

    const mapOptions = {
      center: { lat: 5.6512026, lng: -0.1598935 },
      zoom: 13,
    }

    mapOptions['fullscreenControl'] = false
    mapOptions['streetViewControl'] = false
    mapOptions['mapTypeControl'] = false

    if (disableGesture) {
      mapOptions['gestureHandling'] = 'none'
    }

    const mapView = regionViewRef.current

    if (!mapView) return

    // eslint-disable-next-line no-undef
    const map = new google.maps.Map(mapView, mapOptions)

    // eslint-disable-next-line no-undef
    geocoder.current = new google.maps.Geocoder()

    if (!displayOnly) {
      const placeAutoCompleteInput = placeAutoCompleteRef.current

      // eslint-disable-next-line no-undef
      const autoComplete = new google.maps.places.Autocomplete(placeAutoCompleteInput)

      autoComplete.bindTo('bounds', map)

      autoComplete.setFields(['geometry', 'name', 'place_id'])

      // eslint-disable-next-line no-undef
      map.controls[google.maps.ControlPosition.TOP_LEFT].push(placeAutoCompleteInput)

      autoComplete.addListener('place_changed', () => {
        const place = autoComplete.getPlace()

        if (!place.geometry || !place.geometry.location) {
          return
        }

        if (place.geometry.viewport) {
          map.fitBounds(place.geometry.viewport)
        } else {
          map.setCenter(place.geometry.location)
          map.setZoom(17)
        }
      })
    }

    // eslint-disable-next-line no-undef
    baseMarker.current = new google.maps.Marker({
      label: {
        className: 'bg-primary text-white p-2 rounded-3 fw-bold',
        text: label,
      },
      map: map,
      position: map.getCenter(),
      text: label,
    })

    map.addListener('center_changed', () => {
      const center = map.getCenter()
      const latLng = { lat: center.lat(), lng: center.lng() }

      baseMarker.current.setPosition(latLng)

      clearTimeout(reverseGeocodeTimeout.current)

      reverseGeocodeTimeout.current = setTimeout(() => {
        geocoder.current.geocode({ location: latLng }, (results, status) => {
          if (status === 'OK' && results.length > 0) {
            const result = results[0]

            const { address_components } = result

            // as at the time this was being developed, I'm skeptical about the
            // correctness of how the address components are derived... due to
            // how the results are returned by Google
            const street = address_components[0].long_name

            // in future, the region will be based on the boundaries defined by Adeton
            const region =
              address_components[address_components.length - 3]?.long_name || 'Unknown region'
            const country = address_components[address_components.length - 1].short_name

            const address = {
              country,
              gps: {
                coordinates: [latLng.lat, latLng.lng],
                type: 'Point',
              },
              region,
              street,
            }

            onSelectAddress(address)
          } else {
            onSelectAddress(null)
          }
        })
      }, 1000)
    })

    setMap(map)
  }, [disableGesture, displayOnly, googleApiKey, label, onSelectAddress])

  function selectLatLng(latLng) {
    map.setCenter(latLng)
    map.setZoom(13)
  }

  function clearGeolocationError() {
    setGeolocationError('')
  }

  function handleLocationFailed() {
    setGeolocationError(
      'Failed to get your location. Allow this site to use your location or move map manually to your address.'
    )
  }

  function handleLocationProvision(position) {
    const { latitude: lat, longitude: lng } = position.coords

    // move camera to this position
    selectLatLng({ lat, lng })
  }

  function useCurrentLocation() {
    clearGeolocationError()

    if (!navigator.geolocation) {
      setGeolocationError('Cannot get your location with your browser. Please try another browser.')
    } else {
      navigator.geolocation.getCurrentPosition(handleLocationProvision, handleLocationFailed)
    }
  }

  useEffect(() => {
    setUpMap()
  }, [setUpMap])

  useEffect(() => {
    pickupMarker.current?.setMap(null)

    if (persistentMarker && map) {
      // eslint-disable-next-line no-undef
      pickupMarker.current = new google.maps.Marker({
        label: {
          className: 'bg-secondary text-white p-2 rounded-3 fw-bold',
          text: persistentMarker.label,
        },
        map: map,
        position: { lat: persistentMarker.coordinate[0], lng: persistentMarker.coordinate[1] },
        text: persistentMarker.label,
      })

      map.setZoom(13)
    }
  }, [persistentMarker, map])

  useEffect(() => {
    if (map && address && !defaultCenterSet) {
      const latLng = {
        lat: address.gps.coordinates[0],
        lng: address.gps.coordinates[1],
      }

      baseMarker.current.setPosition(latLng)

      map.setCenter(latLng)

      setDefaultCenterSet(true)
    }
  }, [defaultCenterSet, map, address])

  return (
    <>
      {!displayOnly && (
        <>
          <p className="text-muted mb-0">Display Address</p>
          {address ? (
            <address>
              {address.street} <br />
              {address.region}
            </address>
          ) : (
            <div>
              <address>
                <span className="text-danger">Address not set</span> <br /> &nbsp;
              </address>
            </div>
          )}
        </>
      )}

      {!displayOnly && (
        <p>
          <small className="text-muted">
            Move map until the pin is in your location or use the{' '}
            <strong>Use my current location</strong> button below to use your current GPS location.
          </small>
        </p>
      )}

      <div
        className="bg-light w-full ratio ratio-16x9-lg ratio-3x4"
        id="region-view"
        ref={regionViewRef}
      ></div>

      {Boolean(geolocationError) && (
        <p className="alert alert-danger p-2 mt-3">
          <FontAwesomeIcon icon={faExclamation} /> &nbsp; {geolocationError}
        </p>
      )}

      {!displayOnly ? (
        <>
          <div className="mt-3">
            <button
              className="btn btn-secondary text-white"
              type="button"
              onClick={useCurrentLocation}
            >
              <FontAwesomeIcon icon={faChevronRight} /> &nbsp; Use my current location
            </button>
          </div>

          <small className="text-muted">
            You will have to allow your browser to share your location. Note, this will not be
            totally accurate. Use map to adjust properly.
          </small>
        </>
      ) : (
        <div className="w-100"></div>
      )}

      <div className="d-none">
        <input
          type="text"
          className="form-control mt-2 w-75 mx-2"
          placeholder="Search for your address"
          id="place-auto-complete"
          ref={placeAutoCompleteRef}
        />
      </div>
    </>
  )
}
